import { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Alert, Paper, Divider,
  CircularProgress, Chip, IconButton, InputAdornment,
} from '@mui/material';
import {
  WhatsApp, Save, Visibility, VisibilityOff, CheckCircle,
  ContentCopy, Wifi, WifiOff, Refresh,
} from '@mui/icons-material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function WhatsAppConfig() {
  const [config, setConfig] = useState({
    phone_number_id: '', access_token: '', waba_id: '',
    webhook_verify_token: '', numero_display: '', activo: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verificando, setVerificando] = useState(false);
  const [conexion, setConexion] = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState(false);

  const webhookUrl = `${window.location.origin}/api/whatsapp/webhook`;

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const res = await axios.get(`${API}/api/whatsapp/config`, { headers: headers() });
      if (res.data.data) setConfig(prev => ({ ...prev, ...res.data.data }));
    } catch (e) {
      setError(e.response?.data?.error || 'Error cargando configuración');
    } finally { setLoading(false); }
  }

  async function handleSave() {
    if (!config.phone_number_id || !config.webhook_verify_token) {
      setError('Phone Number ID y Webhook Verify Token son requeridos');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/whatsapp/config`, config, { headers: headers() });
      setSuccess('Configuración guardada correctamente');
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) {
      setError(e.response?.data?.error || 'Error guardando');
    } finally { setSaving(false); }
  }

  async function handleVerificar() {
    setVerificando(true);
    setConexion(null);
    try {
      const res = await axios.get(`${API}/api/whatsapp/verificar`, { headers: headers() });
      setConexion(res.data.data);
    } catch (e) {
      setConexion({ conectado: false, error: e.response?.data?.message || e.message });
    } finally { setVerificando(false); }
  }

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 860 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ bgcolor: '#25d366', borderRadius: 2, p: 1, display: 'flex' }}>
          <WhatsApp sx={{ color: 'white', fontSize: 28 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 800 }}>WhatsApp Business</Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            API oficial de Meta — conexión directa sin intermediarios
          </Typography>
        </Box>
        {config.phone_number_id && (
          <Chip label="Configurado ✓" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }} icon={<CheckCircle sx={{ color: '#2e7d32 !important' }} />} />
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Estado de conexión */}
      {conexion && (
        <Alert
          severity={conexion.conectado ? 'success' : 'error'}
          sx={{ mb: 2 }}
          onClose={() => setConexion(null)}
        >
          {conexion.conectado
            ? `✅ Conectado — ${conexion.data?.display_phone_number || ''} (${conexion.data?.verified_name || ''}) · Calidad: ${conexion.data?.quality_rating || '—'}`
            : `❌ Sin conexión: ${conexion.error}`
          }
        </Alert>
      )}

      {/* Webhook URL */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', mb: 0.5 }}>
          URL del Webhook — configura esto en Meta Developers
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontFamily: 'monospace', fontSize: 13, flex: 1, wordBreak: 'break-all', color: '#1565c0' }}>
            {webhookUrl}
          </Typography>
          <IconButton size="small" onClick={() => copyToClipboard(webhookUrl)} title="Copiar">
            <ContentCopy fontSize="small" sx={{ color: copied ? '#25d366' : 'text.secondary' }} />
          </IconButton>
        </Box>
        <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.5 }}>
          En Meta → Tu App → WhatsApp → Configuración → Webhook → Callback URL
        </Typography>
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* Formulario credenciales */}
      <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 2 }}>Credenciales de la API</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
        <TextField
          label="Phone Number ID *" size="small"
          value={config.phone_number_id}
          onChange={e => setConfig(p => ({ ...p, phone_number_id: e.target.value }))}
          helperText="Meta → WhatsApp → Configuración de la API"
          placeholder="123456789012345"
        />
        <TextField
          label="WABA ID (WhatsApp Business Account ID)" size="small"
          value={config.waba_id || ''}
          onChange={e => setConfig(p => ({ ...p, waba_id: e.target.value }))}
          helperText="Requerido para ver las plantillas aprobadas"
          placeholder="123456789012345"
        />
        <TextField
          label="Access Token *" size="small"
          type={showToken ? 'text' : 'password'}
          value={config.access_token}
          onChange={e => setConfig(p => ({ ...p, access_token: e.target.value }))}
          helperText="Token permanente del System User (no el temporal de 24h)"
          sx={{ gridColumn: { sm: '1 / -1' } }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowToken(p => !p)}>
                  {showToken ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        <TextField
          label="Webhook Verify Token *" size="small"
          value={config.webhook_verify_token}
          onChange={e => setConfig(p => ({ ...p, webhook_verify_token: e.target.value }))}
          helperText="Token secreto que defines tú — ponlo también en Meta"
          placeholder="mi-token-secreto-2026"
        />
        <TextField
          label="Número display" size="small"
          value={config.numero_display || ''}
          onChange={e => setConfig(p => ({ ...p, numero_display: e.target.value }))}
          helperText="Para mostrar en la UI — ej: +593 99 123 4567"
          placeholder="+593 99 123 4567"
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
        <Button
          variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
          onClick={handleSave}
          disabled={saving || !config.phone_number_id || !config.webhook_verify_token}
          sx={{ bgcolor: '#25d366', '&:hover': { bgcolor: '#1da851' }, textTransform: 'none', fontWeight: 700 }}
        >
          {saving ? 'Guardando...' : 'Guardar configuración'}
        </Button>
        <Button
          variant="outlined" startIcon={verificando ? <CircularProgress size={16} /> : <Refresh />}
          onClick={handleVerificar} disabled={verificando || !config.phone_number_id}
          sx={{ textTransform: 'none', borderColor: '#25d366', color: '#1da851' }}
        >
          {verificando ? 'Verificando...' : 'Verificar conexión'}
        </Button>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Guía rápida */}
      <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1.5 }}>📋 Guía rápida de configuración</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {[
          { n: 1, txt: 'Ve a developers.facebook.com → Mis Apps → Crear App → Tipo: Empresa → Agrega el producto WhatsApp' },
          { n: 2, txt: 'En WhatsApp → Configuración de la API: copia el Phone Number ID y el WABA ID' },
          { n: 3, txt: 'Crea un System User en Meta Business Suite con permisos de WhatsApp Business. Genera un token permanente (no el de 24h)' },
          { n: 4, txt: `En WhatsApp → Configuración → Webhooks → Callback URL: ${webhookUrl}. Verify Token: el que definiste arriba` },
          { n: 5, txt: 'En el webhook suscríbete al campo "messages". Guarda y verifica la conexión con el botón de arriba' },
          { n: 6, txt: 'Crea plantillas en Meta Business Suite → Herramientas → Plantillas de mensajes. Espera la aprobación (1-2 días) antes de enviar campañas' },
        ].map(({ n, txt }) => (
          <Box key={n} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: '#25d366', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0, mt: 0.25 }}>
              {n}
            </Box>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.5 }}>{txt}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
